/**
 * contact-detail-drawer.tsx — Màn chi tiết khách trượt từ cạnh PHẢI (nút "Chi tiết" cạnh tên khách ở màn Hội thoại):
 * đủ các tab như trang /customers/:id nhưng không rời hội thoại. "Mở trang đầy đủ" sang trang riêng.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ExternalLink, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SideDrawer, SideDrawerClose, SideDrawerContent } from '@/components/ui/side-drawer'
import { useTranslation } from 'react-i18next'
import { ContactDetailView, type DetailTab } from './contact-detail-page'

export function ContactDetailDrawer({ contactId, open, onOpenChange, initialTab = 'overview' }: {
  contactId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  initialTab?: DetailTab
}) {
  const { t } = useTranslation('custDetail')
  const [tab, setTab] = useState<DetailTab>(initialTab)
  useEffect(() => {
    if (open) setTab(initialTab)
  }, [open, initialTab])

  // Rời trang (vd bấm "Nhắn tin" sang hội thoại khác) → đóng ngăn, không để hồ sơ cũ đè lên màn mới.
  const { pathname } = useLocation()
  const firstPath = useRef(pathname)
  useEffect(() => {
    if (pathname !== firstPath.current) {
      firstPath.current = pathname
      onOpenChange(false)
    }
  }, [pathname, onOpenChange])

  return (
    <SideDrawer open={open} onOpenChange={onOpenChange}>
      <SideDrawerContent
        title={t('drawer.title')}
        className="left-auto right-0 w-[min(1180px,100vw)] max-w-none animate-drawer-right pl-0 pr-[env(safe-area-inset-right)] sm:w-[min(1180px,94vw)]"
      >
        <div className="flex shrink-0 items-center gap-2 border-b bg-background px-4 py-2.5">
          <p className="text-sm font-semibold">{t('drawer.title')}</p>
          <Button variant="ghost" size="sm" className="ml-auto h-8 gap-1.5 text-xs" asChild>
            <Link to={`/customers/${contactId}${tab === 'overview' ? '' : `?tab=${tab}`}`}>
              <ExternalLink className="h-3.5 w-3.5" /> {t('drawer.openFull')}
            </Link>
          </Button>
          <SideDrawerClose asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('common:actions.close')}>
              <X className="h-4 w-4" />
            </Button>
          </SideDrawerClose>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto bg-muted/30 p-3 sm:p-5">
          {open && (
            <ContactDetailView
              id={contactId}
              tab={tab}
              onTab={(next) => setTab(next as DetailTab)}
              onDeleted={() => onOpenChange(false)}
              compact
            />
          )}
        </div>
      </SideDrawerContent>
    </SideDrawer>
  )
}
